import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { connect } from 'react-redux';

import { bindActionCreators, Dispatch } from 'redux';
import uuid from 'uuid';

import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Tooltip from '@material-ui/core/Tooltip';

import {
  applyLayout,
  changeLayout,
} from '../../lib/actions/room';
import { logEvent, Event } from '../../lib/analytics';
import { State } from '../../lib/reducers';
import {
  RoomLayout,
  RoomLayoutStreamType,
  RoomLayoutConfig
} from '../../lib/redux_types';
import {
  isAudioOnlyLayout,
  isWebinarLayout,
  amModerator
} from '../../lib/reduxSelectors/room';
import { getWsUserId } from '../../lib/reduxSelectors/websocket';
import CascadingMenu, { CascadingMenuItem } from "../CascadingMenu/CascadingMenu";
import {
  IconLayout,
  IconLayoutGrid,
  IconLayoutAudio,
  IconLayoutWebinar,
} from '../IconSet';


const messages = defineMessages({
  selectToMeLayout: { id: 'selectToMeLayout' },
  selectToAllLayout: { id: 'selectToAllLayout' },
  gridLayout: { id: 'gridLayout' },
  webinarLayout: { id: 'webinarLayout' },
  presentationLayout: { id: 'presentationLayout' },
  audioOnlyLayout: { id: 'audioOnlyLayout' },
  fullscreenLayout: { id: 'fullscreenLayout' },
  chooseLayout: { id: 'chooseLayout' },
});


type MappedProps = {
  myUid: string | null;
  layout: State['room']['layout'];
  layoutConfig: State['room']['layoutConfig'];
  amModerator: boolean;
  allowedLayouts: RoomLayout[] | undefined;
  canChangeOwnLayout: boolean;
}


const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators({
    handleChangeLayout: (layoutType, layoutConfig) => changeLayout(layoutType, layoutConfig),
    handleApplyLayout: () => applyLayout(),
  },
  dispatch
  );


const mapStateToProps = (state: State): MappedProps => {
  // since audio and webinar layouts are always applied to all users, do not
  // allow changing own layout when in audio or webinar. Applying to everyone
  // instead is shown anyway
  const canChangeOwnLayout = (!isAudioOnlyLayout(state) && !isWebinarLayout(state));

  return {
    myUid: getWsUserId(state.websocket),
    layout: state.room.layout,
    layoutConfig: state.room.layoutConfig,
    amModerator: amModerator(state),
    allowedLayouts: state.appconfig.room_options.allowed_layouts,
    canChangeOwnLayout,
  };
};


type Props = {
  disabled?: boolean;
} & MappedProps & ReturnType<typeof mapDispatchToProps>;


function filterAllowedLayouts(menuItems: CascadingMenuItem[], allowedLayouts: MappedProps['allowedLayouts']) {
  if (!allowedLayouts) {
    return menuItems;
  }

  const mapping: { [key: string]: RoomLayout } = {
    'fullscreen': 'fullscreen',
    'presentation': 'featured',
    'grid': 'default',
    'webinar': 'webinar',
    'audio': 'audioonly',
    'lesson': 'lesson',
  };
  return menuItems.filter(
    (item) => {
      const mapped = mapping[item.key as keyof (typeof mapping)];
      if (item.key === 'grid') {
        return true;  // always include the default layout
      }
      return (mapped && allowedLayouts.includes(mapped));
    }
  );
}


function MenuLayout(props: Props) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const { formatMessage } = useIntl();

  const open = Boolean(anchorEl);

  const getPresentationSubItems = (): Array<CascadingMenuItem> => {
    return [
      {
        key: uuid.v4(),
        caption: formatMessage(messages.selectToMeLayout),
        onClick: () => {
          if (props.myUid) {
            const layoutConfig = {
              // eslint-disable-next-line @typescript-eslint/camelcase
              enlarge_video: false,
              // eslint-disable-next-line @typescript-eslint/camelcase
              featured_id: props.myUid,
              // eslint-disable-next-line @typescript-eslint/camelcase
              featured_type: "stream" as RoomLayoutStreamType
            };
            SelectToMe("featured", layoutConfig);
          }
        },
        show: props.canChangeOwnLayout,
      },
      {
        key: uuid.v4(),
        caption: formatMessage(messages.selectToAllLayout),
        onClick: () => {
          if (props.myUid) {
            const currentLayoutConfig = props.layoutConfig;

            const layoutConfig = {
              // eslint-disable-next-line @typescript-eslint/camelcase
              enlarge_video: currentLayoutConfig.enlarge_video || false,
              // eslint-disable-next-line @typescript-eslint/camelcase
              featured_id: currentLayoutConfig.featured_id,
              // eslint-disable-next-line @typescript-eslint/camelcase
              featured_type: currentLayoutConfig.featured_type,
            };

            SelectToAll('featured', layoutConfig);
          }
        },
        show: props.amModerator
      }
    ];
  };

  const getFullScreenSubItems = (): Array<CascadingMenuItem> => {
    return [
      {
        key: uuid.v4(),
        caption: formatMessage(messages.selectToMeLayout),
        onClick: () => {
          if (props.myUid) {
            const layoutConfig = {};
            SelectToMe('fullscreen', layoutConfig);
          }
        },
        show: props.canChangeOwnLayout,
      },
      {
        key: uuid.v4(),
        caption: formatMessage(messages.selectToAllLayout),
        onClick: () => {
          if (props.myUid) {
            const currentLayoutConfig = props.layoutConfig;

            const layoutConfig = {
              // eslint-disable-next-line @typescript-eslint/camelcase
              enlarge_video: currentLayoutConfig.enlarge_video || false,
              // eslint-disable-next-line @typescript-eslint/camelcase
              featured_id: currentLayoutConfig.featured_id,
              // eslint-disable-next-line @typescript-eslint/camelcase
              featured_type: currentLayoutConfig.featured_type,
            };
            SelectToAll('fullscreen', layoutConfig);
          }
        },
        show: props.amModerator
      }
    ];
  };

  const getGridSubItems = (): Array<CascadingMenuItem> => {
    return [
      {
        key: uuid.v4(),
        caption: formatMessage(messages.selectToMeLayout),
        onClick: () => {
          const layoutConfig = {};
          SelectToMe('default', layoutConfig);
        },
        show: props.canChangeOwnLayout,
      },
      {
        key: uuid.v4(),
        caption: formatMessage(messages.selectToAllLayout),
        onClick: () => {
          const layoutConfig = {};
          SelectToAll('default', layoutConfig);
        },
        show: props.amModerator
      }
    ];
  };

  const getMenuItems = (): Array<CascadingMenuItem> => {
    const menuItems: Array<CascadingMenuItem> = [
      {
        key: "fullscreen",
        caption: formatMessage(messages.fullscreenLayout),
        icon: <IconLayoutWebinar size={24} />,
        subMenuItems: getFullScreenSubItems()
      },
      {
        key: "presentation",
        caption: formatMessage(messages.presentationLayout),
        icon: <IconLayout size={24} />,
        subMenuItems: getPresentationSubItems()
      },
      {
        key: "grid",
        caption: formatMessage(messages.gridLayout),
        icon: <IconLayoutGrid size={24} />,
        subMenuItems: getGridSubItems()
      },
      {
        key: "audio",
        caption: formatMessage(messages.audioOnlyLayout),
        icon: <IconLayoutAudio size={24} />,
        onClick: () => SelectToAll('audioonly', {}),
        show: props.amModerator
      },
    ];

    return filterAllowedLayouts(menuItems, props.allowedLayouts);
  };

  const closeMenu = React.useCallback(
    () => {
      setAnchorEl(null);
    }
    , []
  );

  function SelectToMe(layoutType: RoomLayout, layoutConfig: RoomLayoutConfig) {
    props.handleChangeLayout(layoutType, layoutConfig);
    setAnchorEl(null);
    logEvent(Event.CHANGE_ROOM_LAYOUT, { 'layout_set': layoutType, 'apply_to': 'me' });
  }

  function SelectToAll(layoutType: RoomLayout, layoutConfig: RoomLayoutConfig) {
    props.handleChangeLayout(layoutType, layoutConfig);
    props.handleApplyLayout();
    setAnchorEl(null);
    logEvent(Event.CHANGE_ROOM_LAYOUT, { 'layout_set': layoutType, 'apply_to': 'all' });
  }

  const disabled = props.disabled ? true : false;

  return (
    <React.Fragment>
      <ListItem disabled={disabled} button onClick={handleMenu}>
        <Tooltip
          placement="left"
          title={formatMessage(messages.chooseLayout)}
        >
          <ListItemIcon>
            <IconLayout size={28} />
          </ListItemIcon>
        </Tooltip>
      </ListItem>
      <CascadingMenu
        open={open}
        menuItems={getMenuItems()}
        anchorEl={anchorEl}
        onClose={closeMenu}
      />

    </React.Fragment>);

}

export default connect(mapStateToProps, mapDispatchToProps)(MenuLayout);
